import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { DownloadBottom } from '@videoblocks/react-icons';

import AiContentEventFactory from '../../../../../Events/AiContentEventFactory';
import events from '../../../../../Events/Events';
import { SUCCESS_TOAST_ID } from '../../../../../ReactToastify/containers/ContentEditToast';
import { Badge } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Badge/Badge';
import { Button } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import { getPreviewUrl } from '../../../../../common/utils';
import {
  selectJobId,
  selectLastPromptText,
  selectLastSelection,
  selectOutputUrl,
  selectStockItemEdited,
  resetToEdit,
  markResultSeen,
} from '../../../../ContentEdit/ContentEditSlice';
import PromptUsedBox from './components/PromptUsedBox';
import VideoEditFeedback from './components/VideoEditFeedback';
import {
  useVideoMetadata,
  useVideoSelection,
} from './context/VideoEditorContext';

const VideoEditResult = ({ handleClose, contentId }) => {
  const dispatch = useDispatch();
  const resultUrl = useSelector(selectOutputUrl);
  const jobId = useSelector(selectJobId);
  const stockItemEdited = useSelector(selectStockItemEdited);
  const lastPromptText = useSelector(selectLastPromptText);
  const lastSelection = useSelector(selectLastSelection);
  const { setSelection } = useVideoSelection();
  const { videoMetadata } = useVideoMetadata();
  const { isVertical } = videoMetadata ?? {};

  const feedbackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(markResultSeen());
    toast.dismiss(SUCCESS_TOAST_ID);
  }, [dispatch]);

  const originalVideoSrc = stockItemEdited?.previewUrl
    ? stockItemEdited.previewUrl
    : stockItemEdited
    ? getPreviewUrl(stockItemEdited.id, stockItemEdited.contentClass)
    : null;

  const handleRegenerate = () => {
    Telemetry.increment('ai.contentEdit.buttonClick.regenerateWithNewPrompt');
    if (lastSelection) {
      setSelection({
        startTime: lastSelection.startTime,
        duration: lastSelection.duration,
        endTime: lastSelection.startTime + lastSelection.duration,
      });
    }
    dispatch(resetToEdit());
  };

  const handleFeedbackSelect = () => {
    setTimeout(() => {
      feedbackRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="flex-1 flex flex-col justify-center items-center w-full">
      <div className="flex flex-col items-center gap-1 text-center my-10">
        <Text fontSize="2xl" fontWeight="semibold" className="m-0">
          Your edited video is ready.
        </Text>
        <Text fontSize="base" fontWeight="regular" className="m-0">
          Review the result below, then download it or try again. Be sure to
          download before closing this window.
        </Text>
      </div>

      <div className="w-full rounded-xl">
        <div className="flex flex-col bg-gray-900 border border-gray-800 rounded-t-xl p-5 gap-5">
          <div className="flex flex-col md:flex-row gap-5 md:gap-10 relative">
            <div className="flex-1 min-w-0 flex flex-col gap-3">
              <Text
                fontSize="sm"
                fontWeight="semibold"
                className="text-white m-0"
              >
                Original
              </Text>
              <div
                className="rounded-xl border overflow-hidden bg-black"
                style={{
                  aspectRatio: '16 / 9',
                  borderColor: 'rgba(255, 255, 255, 0.20)',
                }}
              >
                {originalVideoSrc ? (
                  <video
                    src={originalVideoSrc}
                    className={`block w-full h-full ${
                      isVertical ? 'object-contain' : 'object-cover'
                    }`}
                    style={{ aspectRatio: '16 / 9' }}
                    preload="metadata"
                    controls
                    muted
                  />
                ) : (
                  <div className="w-full h-full" />
                )}
              </div>
            </div>

            <div
              className="hidden md:flex absolute"
              style={{
                left: '50%',
                top: 'calc(50% + 16px)',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <Badge
                text="vs"
                shape="rounded"
                size="medium"
                color="neutral"
                fontWeight="semibold"
                textCase="lowercase"
                className="bg-white-3 text-white border bg-none"
                style={{
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                }}
              />
            </div>

            <div className="flex-1 min-w-0 flex flex-col gap-3">
              <Text fontSize="sm" fontWeight="semibold" className="m-0">
                <span className="text-white">Edited </span>
                <span className="text-yellow-500">(AI Result)</span>
              </Text>
              <div
                className="rounded-xl border-yellow-500 overflow-hidden bg-black"
                style={{ aspectRatio: '16 / 9', borderWidth: '3px' }}
              >
                <video
                  src={resultUrl}
                  className={`block w-full h-full ${
                    isVertical ? 'object-contain' : 'object-cover'
                  }`}
                  style={{ aspectRatio: '16 / 9' }}
                  preload="metadata"
                  controls
                  autoPlay
                  loop
                  muted
                />
              </div>
            </div>
          </div>

          {lastPromptText && (
            <div className="lg:px-7 lg:pb-5">
              <PromptUsedBox promptText={lastPromptText} />
            </div>
          )}
        </div>

        <div className="bg-gray-800 py-7 flex flex-col items-center gap-2 rounded-b-xl">
          <Button
            iconPrefix={<DownloadBottom />}
            variant="primary"
            style={{ gap: '0.5rem' }}
            href={`/api/video/generate-edit/download?jobId=${jobId}`}
            download="video.mp4"
            onClick={() => {
              const aiContentDownloadEvent =
                AiContentEventFactory.createDownloadEvent(contentId);
              events.produce(aiContentDownloadEvent);
            }}
          >
            Download edited video
          </Button>
          <Button
            variant="secondary"
            buttonClassName="flex items-center px-4"
            onClick={handleRegenerate}
          >
            Regenerate with new prompt
          </Button>
        </div>
      </div>

      <div ref={feedbackRef} className="mt-8 w-full">
        <VideoEditFeedback
          handleClose={handleClose}
          contentId={contentId}
          onFeedbackSelect={handleFeedbackSelect}
        />
      </div>
    </div>
  );
};

export default VideoEditResult;
